import type React from 'react';
import ReactECharts from 'echarts-for-react';
import {Typography} from '@mui/material';
import {perUnit} from '../../domain/unitPrice';
import type {BenchmarkGroup} from '../../types/benchmarkTypes';

type Props = {skus: BenchmarkGroup[]; price: (v: number | null) => string};

const short = (s: string) => (s.length > 28 ? `${s.slice(0, 27)}…` : s);

/**
 * Price breakdown per SKU: how far the average price paid sits above the best internal price, and, where a contract
 * price exists, the discount against it. Units differ per SKU, so bars are percentages; prices are in the tooltip.
 */
export const SkuPriceChart: React.FC<Props> = ({skus, price}) => {
    if (skus.length === 0) return <Typography variant="body2" color="text.secondary">No items to show.</Typography>;
    const rows = [...skus].reverse(); // largest spend on top
    const aboveBest = rows.map(s => (s.bestPrice ? Math.round(1000 * (s.averagePrice - s.bestPrice) / s.bestPrice) / 10 : 0));
    const discount = rows.map(s => s.discountPct);
    const hasContract = discount.some(d => d !== null);

    return (
        <ReactECharts style={{height: Math.max(220, rows.length * 30 + 60)}} notMerge option={{
            grid: {left: 200, right: 40, top: 30, bottom: 24},
            legend: {top: 0, data: ['Paid above best internal price', ...(hasContract ? ['Discount vs contract price'] : [])]},
            tooltip: {
                trigger: 'axis', axisPointer: {type: 'shadow'},
                formatter: (params: {dataIndex: number}[]) => {
                    const s = rows[params[0].dataIndex];
                    return [
                        `<b>${s.label}</b>`,
                        `Average paid: ${perUnit(price(s.averagePrice), s.unit)}`,
                        `Best internal: ${perUnit(price(s.bestPrice), s.unit)} (${s.bestSite})`,
                        s.referencePrice !== null ? `Contract price: ${perUnit(price(s.referencePrice), s.unit)}` : 'No contract price',
                    ].join('<br/>');
                },
            },
            xAxis: {type: 'value', axisLabel: {formatter: '{value}%'}},
            yAxis: {type: 'category', data: rows.map(s => short(s.label)), axisLabel: {fontSize: 11}},
            series: [
                {name: 'Paid above best internal price', type: 'bar', data: aboveBest, itemStyle: {color: '#ED6C02'}},
                ...(hasContract ? [{name: 'Discount vs contract price', type: 'bar', data: discount, itemStyle: {color: '#2E7D32'}}] : []),
            ],
        }}/>
    );
};
